import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Users, Music, Award, Heart, Drum, Sparkles, Baby } from 'lucide-react';
import { theme } from '@/styles/theme';
import { Container, Section, Grid } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { FadeInWhenVisible } from '@/components/animations/FadeInWhenVisible';
import { CinematicHero } from '@/components/animations/CinematicHero';
import { Link } from 'react-router-dom';

// Feature Section
const FeatureSection = styled(Section)`
  background: linear-gradient(
    180deg,
    ${theme.colors.neutral.white} 0%,
    ${theme.colors.secondary.ivory} 100%
  );
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-family: ${theme.fonts.heading};
  font-size: clamp(2rem, 5vw, 3.75rem);
  color: ${theme.colors.primary.navy};
  margin-bottom: ${theme.spacing[4]};
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.neutral.gray};
  margin-bottom: ${theme.spacing[12]};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const FeatureCard = styled(motion(Card))`
  background: ${theme.colors.neutral.white};
  text-align: center;
  transition: all ${theme.transitions.base};
  border: 1px solid transparent;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    border-color: ${theme.colors.primary.gold};
    box-shadow: ${theme.shadows.lg};
  }
`;

const IconWrapper = styled(motion.div)`
  width: 90px;
  height: 90px;
  margin: 0 auto ${theme.spacing[5]};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.primary.navy};
  border-radius: ${theme.borderRadius.full};
  color: ${theme.colors.primary.gold};
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes['2xl']};
  font-family: ${theme.fonts.heading};
  color: ${theme.colors.primary.navy};
  margin-bottom: ${theme.spacing[3]};
  font-weight: ${theme.fontWeights.bold};
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.neutral.gray};
  line-height: ${theme.lineHeights.relaxed};
  font-size: ${theme.fontSizes.base};
  flex-grow: 1;
`;

// CTA Section
const CTASection = styled(Section)`
  background: ${theme.colors.primary.navy};
  color: ${theme.colors.neutral.white};
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.3;
  }
`;

const CTATitle = styled(motion.h2)`
  color: ${theme.colors.neutral.white};
  font-family: ${theme.fonts.heading};
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: ${theme.spacing[4]};
`;

const CTAText = styled(motion.p)`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.primary.lightGold};
  margin-bottom: ${theme.spacing[8]};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${theme.lineHeights.relaxed};
`;

export const HomePage = () => {
  return (
    <>
      {/* Cinematic hero: ensemble photo under the softened ribbon veil */}
      <CinematicHero />

      {/* Features Section */}
      <FeatureSection>
        <Container>
          <FadeInWhenVisible>
            <SectionTitle>Waarom Kiezen Voor KPH Sint-Cecilia?</SectionTitle>
            <SectionSubtitle>
              Ontdek wat ons uniek maakt en waarom muzikanten en organisatoren voor ons kiezen
            </SectionSubtitle>
          </FadeInWhenVisible>

          <Grid columns={4}>
            <FadeInWhenVisible delay={0.1}>
              <FeatureCard elevated whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Award size={45} />
                  </IconWrapper>
                  <FeatureTitle>Traditie</FeatureTitle>
                  <FeatureDescription>
                    Meer dan een eeuw aan muzikale culturele bijdragen aan ons publiek in optredens en optochten.
                  </FeatureDescription>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.2}>
              <FeatureCard elevated whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Users size={45} />
                  </IconWrapper>
                  <FeatureTitle>Vier Ensembles</FeatureTitle>
                  <FeatureDescription>
                    De Harmonie, Précilia, Popcilia en Trommelkorps - voor elke leeftijd en elk niveau een plaats.
                  </FeatureDescription>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.3}>
              <FeatureCard elevated whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Music size={45} />
                  </IconWrapper>
                  <FeatureTitle>Diverse Optredens</FeatureTitle>
                  <FeatureDescription>
                    Van klassieke concerten tot moderne arrangementen, brengen wij gevarieerde stukken voor ons publiek.
                  </FeatureDescription>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.4}>
              <FeatureCard elevated whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Heart size={45} />
                  </IconWrapper>
                  <FeatureTitle>Eén Grote Familie</FeatureTitle>
                  <FeatureDescription>
                    Bij ons ontstaan hechte vriendschappen en vinden ook regelmatig activiteiten plaats naast het musiceren.
                  </FeatureDescription>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>
          </Grid>
        </Container>
      </FeatureSection>

      {/* Groups Section */}
      <Section background={theme.colors.neutral.white}>
        <Container>
          <FadeInWhenVisible>
            <SectionTitle>Onze Groepen</SectionTitle>
            <SectionSubtitle>
              Van ervaren muzikanten tot startende talenten - iedereen vindt hier zijn plaats
            </SectionSubtitle>
          </FadeInWhenVisible>

          <Grid columns={4}>

            <FadeInWhenVisible delay={0.3}>
              <FeatureCard interactive whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Baby size={45} />
                  </IconWrapper>
                  <FeatureTitle>Popcilia</FeatureTitle>
                  <FeatureDescription>
                    Onze jongste groep waar kinderen spelenderwijs kennismaken met muziek en hun talenten kunnen ontdekken.
                  </FeatureDescription>
                  <div style={{ marginTop: theme.spacing[6] }}>
                    <Link to="/groepen#popcilia">
                      <Button variant="ghost">Meer Info →</Button>
                    </Link>
                  </div>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.2}>
              <FeatureCard interactive whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Sparkles size={45} />
                  </IconWrapper>
                  <FeatureTitle>Précilia</FeatureTitle>
                  <FeatureDescription>
                    Onze starters-harmonie waar startende muzikanten de kans krijgen om hun muzikale vaardigheden te ontwikkelen.
                  </FeatureDescription>
                  <div style={{ marginTop: theme.spacing[6] }}>
                    <Link to="/groepen#precilia">
                      <Button variant="ghost">Meer Info →</Button>
                    </Link>
                  </div>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>



            <FadeInWhenVisible delay={0.1}>
              <FeatureCard interactive whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Music size={45} />
                  </IconWrapper>
                  <FeatureTitle>De Harmonie</FeatureTitle>
                  <FeatureDescription>
                    Ons hoofdorkest dat een breed repertoire uitvoert van klassiek tot modern.
                  </FeatureDescription>
                  <div style={{ marginTop: theme.spacing[6] }}>
                    <Link to="/groepen#harmonie">
                      <Button variant="ghost">Meer Info →</Button>
                    </Link>
                  </div>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>

            <FadeInWhenVisible delay={0.4}>
              <FeatureCard interactive whileHover={{ y: -8 }}>
                <CardContent>
                  <IconWrapper>
                    <Drum size={45} />
                  </IconWrapper>
                  <FeatureTitle>Trommelkorps</FeatureTitle>
                  <FeatureDescription>
                    Ons slagwerkensemble dat zorgt voor ritme en energie bij optredens en uitstappen.
                  </FeatureDescription>
                  <div style={{ marginTop: theme.spacing[6] }}>
                    <Link to="/groepen#trommelkorps">
                      <Button variant="ghost">Meer Info →</Button>
                    </Link>
                  </div>
                </CardContent>
              </FeatureCard>
            </FadeInWhenVisible>
          </Grid>
        </Container>
      </Section>

      {/* CTA Section */}
      <CTASection>
        <Container>
          <FadeInWhenVisible>
            <CTATitle>Maak Deel Uit van Onze Muzikale Reis</CTATitle>
            <CTAText>
              Word lid van onze harmonie en ontwikkel je muzikaal talent, of nodig ons uit om uw evenement
              te verrijken met onze muziek. Neem contact op en ontdek onze mogelijkheden!
            </CTAText>
            <Link to="/contact">
              <Button size="lg" variant="secondary">
                Neem Contact Op
              </Button>
            </Link>
          </FadeInWhenVisible>
        </Container>
      </CTASection>
    </>
  );
};
